/**
 * El ticket de venta en curso, fuera del arbol de rutas.
 *
 * POR QUE NO ES ESTADO DE UNA PANTALLA
 * ------------------------------------
 * La condicion normal de trabajo en el local es la interrupcion: se empieza a
 * cargar una venta y entra un WhatsApp preguntando un precio, o alguien deja un
 * equipo roto en el mostrador. Si el ticket viviera en el `useState` de la
 * pantalla de ventas, navegar a cualquier otro lado lo borraria.
 *
 * Y peor: un reload accidental —el celular que se queda sin bateria, el
 * navegador que descarta la pestaña— tambien. Por eso ademas se persiste.
 *
 * QUE SE PERSISTE Y QUE NO
 * ------------------------
 * Se guarda la LISTA: que productos, cuantos, a quien. Eso es trabajo de
 * tipeo que duele perder.
 *
 * NO se guardan los PAGOS. Un medio de pago a medio cargar que reaparece
 * despues de un reload es peligroso: la persona puede creer que ya cobro algo
 * que nunca cobro. Los pagos se vuelven a declarar siempre, con la plata
 * delante.
 *
 * Pero ir a otra pantalla y volver no es un reload: la persona sigue ahi, con
 * la plata en la mano. Los pagos vivian en el estado del ticket y se perdian
 * al navegar, aunque la lista quedara. Ahora quedan en MEMORIA del modulo
 * (`useCobroEnMemoria`): sobreviven a la navegacion, no a un reload.
 */
import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";

import { esUnidadUsada, type Moneda } from "@tora/shared";
import { nombreCompleto } from "@/components/catalogo/nombre";
import type { ItemTicket } from "@/components/venta/TicketVenta";

const CLAVE = "tora:ticket";

/**
 * Lo que el ticket necesita de un resultado de busqueda para armar la linea.
 *
 * Es UNA sola funcion para los dos caminos al ticket —el campo de carga y el
 * ⌘K— porque el ⌘K armaba la linea por su cuenta con precio 0 («el backend lo
 * resuelve al emitir»), y el ticket totalizaba con ese cero: Cobrar $ 0,00 y
 * no habia forma de cobrar. Tampoco sabia si el producto era a pedido.
 */
export interface ResultadoParaTicket {
  producto_id: number;
  nombre: string;
  precio_ars: string;
  precio_usd: string;
  precio_moneda: Moneda;
  stock_actual?: string | number | null;
  disponibilidad?: "DISPONIBLE" | "A_PEDIDO" | "SIN_STOCK";
  /**
   * El flag del producto (0115). No alcanza con `disponibilidad`: dice
   * DISPONIBLE apenas hay una, y un iPhone a pedido con stock 1 vendido de a 2
   * deja un encargo igual (`emitir_venta`, 0037).
   */
  a_pedido?: boolean | null;
  /**
   * Para saber si es un equipo usado concreto, que es uno solo (`esUnidadUsada`,
   * la misma regla que `es_unidad_usada` de la base). `buscar_precio` trae
   * `es_usado` y `bateria_pct`; el IMEI no, pero un equipo del local siempre
   * tiene la bateria medida.
   */
  es_usado?: boolean | null;
  bateria_pct?: number | null;
  imei?: string | null;
  /** Para el nombre completo del usado en la linea: dos unidades iguales se distinguen. */
  color?: string | null;
  memoria_gb?: number | null;
  /** El costo y el margen en pesos, de `buscar_precio`. Ver `usadoSinPrecio`. */
  costo_ars?: string | number | null;
  margen_ars?: string | number | null;
}

/**
 * Un usado al que nadie le puso precio de venta: sin `precio_manual` y con
 * markup cero, el trigger de precios lo iguala al costo, y el mostrador lo
 * ofrecia a lo que se pago por el, sin ganancia y sin aviso. Pasa con el
 * equipo que entra en parte de pago con «Precio de venta» vacio, que el
 * dialogo invita a dejar para despues. Se reconoce por lo que trae la
 * busqueda: usado, con costo, y margen exactamente cero.
 */
export function usadoSinPrecio(
  p: Pick<ResultadoParaTicket, "es_usado" | "costo_ars" | "margen_ars">,
): boolean {
  if (p.es_usado !== true) return false;
  if (p.costo_ars == null || p.margen_ars == null) return false;
  return Number(p.costo_ars) > 0 && Number(p.margen_ars) === 0;
}

export function itemDeResultado(p: ResultadoParaTicket): Omit<ItemTicket, "cantidad"> {
  const unidad = esUnidadUsada({
    es_usado: p.es_usado === true,
    imei: p.imei ?? null,
    bateria_pct: p.bateria_pct ?? null,
  });
  return {
    producto_id: p.producto_id,
    // Un usado va con su color y su bateria, y con el IMEI en la linea: dos
    // «IPHONE 17 PRO MAX 256 GB (USADO)» se veian identicos en el ticket, el
    // vendedor no podia comprobar cual cobraba y la venta quedaba sin IMEI.
    nombre: unidad
      ? nombreCompleto({
          nombre: p.nombre,
          color: p.color ?? null,
          memoria_gb: p.memoria_gb ?? null,
          bateria_pct: p.bateria_pct ?? null,
        })
      : p.nombre,
    ...(unidad && p.imei ? { serie_imei: p.imei } : {}),
    precio_unitario: Number(p.precio_moneda === "ARS" ? p.precio_ars : p.precio_usd),
    precio_moneda: p.precio_moneda,
    // Sin el flag (una respuesta de antes de 0115), lo que dice la disponibilidad.
    a_pedido: p.a_pedido ?? p.disponibilidad === "A_PEDIDO",
    ...(unidad ? { es_unidad: true } : {}),
    ...(p.stock_actual !== undefined && p.stock_actual !== null
      ? { stock: Number(p.stock_actual) }
      : {}),
  };
}

/**
 * Cuantas unidades de la linea quedan como ENCARGO: lo a pedido que excede el
 * stock, con la misma cuenta que `emitir_venta` (0037). Antes el ticket miraba
 * solo el flag de la linea, que salia de la disponibilidad: con stock 1 y
 * cantidad 2 no lo marcaba a pedido, no pedia cliente y decia «1 sale sin
 * stock», pero la base igual dejaba un encargo, y sin nadie a quien avisarle.
 * Sin el stock (un ticket guardado de antes), lo a pedido cuenta entero.
 */
export function encargadosDe(item: Pick<ItemTicket, "a_pedido" | "stock" | "cantidad">): number {
  if (!item.a_pedido) return 0;
  if (item.stock === undefined) return item.cantidad;
  return Math.max(item.cantidad - Math.max(item.stock, 0), 0);
}

export interface Ticket {
  items: ItemTicket[];
  moneda: Moneda;
  clienteId: number | null;
  clienteNombre: string | null;
  /**
   * "Esta venta es de mostrador" dicho a proposito, que no es lo mismo que
   * `clienteId === null`.
   *
   * Sin este campo no se puede distinguir "todavia no contesto a quien le
   * vende" de "contesto que a nadie en particular", y esa diferencia es toda
   * la razon de ser del paso previo: una venta queda sin cliente porque se
   * decidio, no porque nadie pregunto.
   */
  mostrador: boolean;
  /** Descuento sobre el total del comprobante, aparte del de cada linea. */
  descuentoTipo: "PORCENTAJE" | "MONTO" | null;
  descuentoValor: number;
}

const TICKET_VACIO: Ticket = {
  items: [],
  moneda: "ARS",
  clienteId: null,
  clienteNombre: null,
  mostrador: false,
  descuentoTipo: null,
  descuentoValor: 0,
};

function leer(): Ticket {
  try {
    const bruto = localStorage.getItem(CLAVE);
    if (!bruto) return TICKET_VACIO;
    const t = JSON.parse(bruto) as Partial<Ticket>;
    // Se valida la forma en vez de confiar: el contenido puede venir de una
    // version anterior del formato, y un ticket corrupto no debe romper la app.
    if (!Array.isArray(t.items)) return TICKET_VACIO;
    return {
      items: t.items.filter(
        (i): i is ItemTicket =>
          typeof i?.producto_id === "number" &&
          typeof i?.nombre === "string" &&
          typeof i?.cantidad === "number" &&
          i.cantidad > 0,
      ),
      moneda: t.moneda === "USD" ? "USD" : "ARS",
      clienteId: typeof t.clienteId === "number" ? t.clienteId : null,
      clienteNombre: typeof t.clienteNombre === "string" ? t.clienteNombre : null,
      mostrador: t.mostrador === true,
      descuentoTipo:
        t.descuentoTipo === "PORCENTAJE" || t.descuentoTipo === "MONTO" ? t.descuentoTipo : null,
      descuentoValor: typeof t.descuentoValor === "number" ? t.descuentoValor : 0,
    };
  } catch {
    // localStorage puede tirar en modo privado o con el almacenamiento
    // bloqueado. Un ticket vacio es peor que uno guardado, pero muchisimo mejor
    // que una pantalla en blanco.
    return TICKET_VACIO;
  }
}

function guardar(t: Ticket): void {
  try {
    // Se guarda tambien el ticket sin productos que YA tiene destinatario: si
    // no, un reload entre elegir el cliente y cargar el primer item vuelve a
    // preguntar, y el paso previo pasa de ayuda a molestia.
    const vacio = t.items.length === 0 && t.clienteId === null && !t.mostrador;
    if (vacio) localStorage.removeItem(CLAVE);
    else localStorage.setItem(CLAVE, JSON.stringify(t));
  } catch {
    /* sin persistencia, el ticket sigue vivo en memoria */
  }
}

/**
 * Suscriptores del ticket. Es un store minimo a mano en vez de una libreria:
 * hay un solo ticket y cuatro operaciones, y sumar una dependencia para eso
 * cuesta mas de lo que ahorra.
 */
const escuchas = new Set<(t: Ticket) => void>();
let actual: Ticket | null = null;

function estado(): Ticket {
  actual ??= leer();
  return actual;
}

function actualizar(fn: (t: Ticket) => Ticket): void {
  actual = fn(estado());
  guardar(actual);
  for (const e of escuchas) e(actual);
}

/**
 * La llave de idempotencia de la venta en curso.
 *
 * Nace con el ticket y muere cuando el ticket se vacia, o sea cuando la venta
 * se emitio. Esa vida util es exactamente la que hace falta: mientras el
 * ticket sea el mismo, mandarlo dos veces es el MISMO intento —el vendedor que
 * aprieta de nuevo porque se corto la red— y el servidor tiene que emitir una
 * sola venta. Ver 0071.
 *
 * Vive en un ref y no en el estado persistido: si sobreviviera a un reload
 * seguiria siendo valida, pero tampoco hace falta, porque el riesgo real es el
 * reintento inmediato de una peticion que se corto.
 */
const nuevaClave = (): string => crypto.randomUUID();

/**
 * Lo del cobro en curso que no va a localStorage (ver arriba): los pagos, el
 * equipo en parte de pago, la propina. Se vacia junto con el ticket.
 */
const cobroEnMemoria = new Map<string, unknown>();

/**
 * Un `useState` que, con `recordar`, sobrevive a desmontar la pantalla. Sin
 * `recordar` es un `useState` comun: asi un ticket que se dibuja suelto (los
 * tests, otra pantalla) no hereda pagos de otro.
 */
export function useCobroEnMemoria<T>(
  clave: string,
  inicial: T,
  recordar: boolean,
): [T, (v: SetStateAction<T>) => void] {
  const [valor, setValor] = useState<T>(() =>
    recordar && cobroEnMemoria.has(clave) ? (cobroEnMemoria.get(clave) as T) : inicial,
  );
  const fijar = useCallback(
    (v: SetStateAction<T>) =>
      setValor((previo) => {
        const nuevo = typeof v === "function" ? (v as (p: T) => T)(previo) : v;
        if (recordar) cobroEnMemoria.set(clave, nuevo);
        return nuevo;
      }),
    [clave, recordar],
  );
  return [valor, fijar];
}

export function useTicket() {
  const clave = useRef(nuevaClave());
  const [ticket, setTicket] = useState<Ticket>(estado);

  useEffect(() => {
    escuchas.add(setTicket);
    // Otra pestaña puede haber cambiado el ticket. Sin esto, dos pestañas
    // abiertas se pisan y una termina cobrando una lista vieja.
    const onStorage = (e: StorageEvent) => {
      if (e.key === CLAVE) {
        actual = leer();
        setTicket(actual);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      escuchas.delete(setTicket);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const agregar = useCallback((item: Omit<ItemTicket, "cantidad"> & { cantidad?: number }) => {
    actualizar((t) => {
      const existente = t.items.find((i) => i.producto_id === item.producto_id);
      if (existente) {
        // Agregar dos veces el mismo producto suma cantidad en vez de duplicar
        // la linea: es lo que espera quien esta cargando rapido. Menos un
        // equipo usado, que es uno solo: se queda en 1.
        return {
          ...t,
          items: t.items.map((i) =>
            i.producto_id === item.producto_id
              ? {
                  ...i,
                  cantidad: i.es_unidad || item.es_unidad ? 1 : i.cantidad + (item.cantidad ?? 1),
                }
              : i,
          ),
        };
      }
      return { ...t, items: [...t.items, { ...item, cantidad: item.cantidad ?? 1 }] };
    });
  }, []);

  /** Cambia el precio, el descuento o el IMEI de una linea ya cargada. */
  const editarItem = useCallback(
    (productoId: number, cambios: Partial<Omit<ItemTicket, "producto_id">>) => {
      actualizar((t) => ({
        ...t,
        items: t.items.map((i) => (i.producto_id === productoId ? { ...i, ...cambios } : i)),
      }));
    },
    [],
  );

  const cambiarCantidad = useCallback((productoId: number, cantidad: number) => {
    actualizar((t) => ({
      ...t,
      items:
        cantidad <= 0
          ? t.items.filter((i) => i.producto_id !== productoId)
          : t.items.map((i) => (i.producto_id === productoId ? { ...i, cantidad } : i)),
    }));
  }, []);

  const quitar = useCallback((productoId: number) => {
    actualizar((t) => ({ ...t, items: t.items.filter((i) => i.producto_id !== productoId) }));
  }, []);

  const cambiarDescuento = useCallback((tipo: "PORCENTAJE" | "MONTO" | null, valor: number) => {
    actualizar((t) => ({ ...t, descuentoTipo: tipo, descuentoValor: valor }));
  }, []);

  const cambiarMoneda = useCallback((moneda: Moneda) => {
    actualizar((t) => ({ ...t, moneda }));
  }, []);

  const asignarCliente = useCallback((id: number | null, nombre: string | null) => {
    // Elegir un cliente contesta la pregunta del paso previo: deja de ser una
    // venta de mostrador.
    actualizar((t) => ({ ...t, clienteId: id, clienteNombre: nombre, mostrador: false }));
  }, []);

  /** Contestar "no es para nadie en particular" — o desdecirse y volver a preguntar. */
  const elegirMostrador = useCallback((valor: boolean) => {
    actualizar((t) => ({ ...t, mostrador: valor }));
  }, []);

  const vaciar = useCallback(() => {
    actualizar(() => TICKET_VACIO);
    cobroEnMemoria.clear();
    // Ticket nuevo, llave nueva. Si no se regenerara, la venta siguiente
    // llegaria con la llave de la anterior y el servidor la tomaria por un
    // reintento: devolveria el comprobante viejo y la venta nueva no se
    // emitiria nunca.
    clave.current = nuevaClave();
  }, []);

  return {
    ticket,
    claveIdempotencia: clave.current,
    agregar,
    editarItem,
    cambiarCantidad,
    quitar,
    cambiarMoneda,
    cambiarDescuento,
    asignarCliente,
    elegirMostrador,
    vaciar,
  };
}
